import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { StopButton } from "./Shell";
import BreathingPacer from "./BreathingPacer";
import ResponsivePacer from "./ResponsivePacer";
import AudioBreathControls from "./AudioBreathControls";
import AdaptivePacerBanner from "./AdaptivePacerBanner";
import Instructions from "./Instructions";
import useAmbientAudio from "../hooks/useAmbientAudio";
import useBreathAudioCue from "../hooks/useBreathAudioCue";
import { useDemo } from "./DemoContext";
import { pickPatternForSUDS } from "../lib/adaptiveBreathing";

// Mode A — Tap-Based Bilateral Stimulation
// Two huge LEFT/RIGHT targets; tracks timestamped tap log;
// derives tap-rate, alternation %, consistency %, engagement score.

// Static "how to run Mode A" guidance — extracted so we don't allocate a
// fresh array on every render of the session screen.
const HOW_TO_RUN_MODE_A = [
  { strong: "Sit comfortably", text: "both feet flat on the floor, shoulders soft, jaw unclenched." },
  { strong: "Tap LEFT then RIGHT", text: "alternating. Use your fingers, thumbs, or fists — whatever feels natural. Arrow keys (← / →) work too." },
  { strong: "Find your own rhythm", text: "somewhere between one and two taps per second is typical. Slow and steady beats fast and frantic." },
  { strong: "If your mind wanders", text: "notice it without judgment and gently return to the rhythm." },
  { strong: "If anything feels off", text: "the red STOP button (top-right) ends the session immediately." },
];

const DEFAULT_DURATION = 180; // seconds
const PACER_SIZES = { mobile: 160, desktop: 220 };

export default function SessionModeA({ onComplete, onStop, defaultTechniques = [], preSuds = null }) {
  // Adaptive pacer: pick pattern + initial-on based on pre-session SUDS.
  const adaptive = useMemo(
    () => (preSuds !== null && preSuds !== undefined ? pickPatternForSUDS(preSuds) : null),
    [preSuds]
  );

  const [running, setRunning] = useState(false);
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [elapsed, setElapsed] = useState(0);
  const [pulseCue, setPulseCue] = useState(true);
  // Always-on by default — matching breath to the pulsing light is the core
  // vagal-tone mechanic; the user can still hide it from the Audio controls.
  const [breathingOn, setBreathingOn] = useState(true);
  const [breathPattern, setBreathPattern] = useState(adaptive ? adaptive.pattern : "4-4-6-2");
  const audio = useAmbientAudio();
  const breathCue = useBreathAudioCue();

  const startedAtRef = useRef(null); // ISO wall-clock start (for wearable correlation)

  const tapsRef = useRef([]); // {side: 'L'|'R', t: ms}
  const [tapCount, setTapCount] = useState(0);
  const [leftCount, setLeftCount] = useState(0);
  const [rightCount, setRightCount] = useState(0);
  const [tapRate, setTapRate] = useState(0);
  const [alternationPct, setAlternationPct] = useState(0);
  const [consistencyPct, setConsistencyPct] = useState(0);
  const [softPause, setSoftPause] = useState(false);

  const startRef = useRef(null);
  const tickRef = useRef(null);

  const tap = useCallback(
    (side) => {
      if (!running) return;
      const now = performance.now();
      tapsRef.current.push({ side, t: now });
      setTapCount(tapsRef.current.length);
      if (side === "L") setLeftCount((c) => c + 1);
      else setRightCount((c) => c + 1);
    },
    [running]
  );

  // keyboard support
  useEffect(() => {
    const handler = (e) => {
      if (!running) return;
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") tap("L");
      else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") tap("R");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [tap, running]);

  // Timer + metric recomputation every 500ms
  useEffect(() => {
    if (!running) return;
    startRef.current = performance.now();
    tickRef.current = setInterval(() => {
      const taps = tapsRef.current;
      const now = performance.now();
      const sec = (now - startRef.current) / 1000;
      setElapsed(Math.min(sec, duration));

      // tap rate (per minute) over last 20s
      const cutoff = now - 20000;
      const recent = taps.filter((x) => x.t >= cutoff);
      const rateBase = Math.min(20, sec);
      setTapRate(rateBase > 0 ? (recent.length / rateBase) * 60 : 0);

      // alternation
      let alt = 0;
      for (let i = 1; i < taps.length; i++)
        if (taps[i].side !== taps[i - 1].side) alt++;
      setAlternationPct(taps.length > 1 ? alt / (taps.length - 1) : 0);

      // consistency: 1 - normalized stddev of intervals
      if (taps.length >= 4) {
        const ivs = [];
        for (let i = 1; i < taps.length; i++) ivs.push(taps[i].t - taps[i - 1].t);
        const mean = ivs.reduce((a, b) => a + b, 0) / ivs.length;
        const variance =
          ivs.reduce((a, b) => a + (b - mean) ** 2, 0) / ivs.length;
        const std = Math.sqrt(variance);
        const cv = mean > 0 ? std / mean : 1;
        setConsistencyPct(Math.max(0, Math.min(1, 1 - cv)));
      }

      // dropout detection: gap > 5s
      if (taps.length > 0 && now - taps[taps.length - 1].t > 5000 && sec > 8) {
        setSoftPause(true);
      } else {
        setSoftPause(false);
      }

      if (sec >= duration) finish();
    }, 500);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, duration]);

  // Pulse cue alternation @ ~1Hz
  useEffect(() => {
    if (!running || !pulseCue) return;
    const id = setInterval(() => {}, 1000);
    return () => clearInterval(id);
  }, [running, pulseCue]);

  const start = () => {
    tapsRef.current = [];
    setTapCount(0);
    setLeftCount(0);
    setRightCount(0);
    setElapsed(0);
    startedAtRef.current = new Date().toISOString();
    setRunning(true);
  };

  const buildMetrics = (isPartial) => {
    const engagement =
      0.4 * consistencyPct +
      0.4 * alternationPct +
      0.2 * Math.min(1, tapsRef.current.length / Math.max(1, duration * 0.5));
    return {
      mode: "A",
      tap_count: tapsRef.current.length,
      tap_rate: Number(tapRate.toFixed(2)),
      left_taps: leftCount,
      right_taps: rightCount,
      alternation_pct: Number(alternationPct.toFixed(3)),
      consistency_pct: Number(consistencyPct.toFixed(3)),
      pacer_pattern: breathingOn ? breathPattern : null,
      duration_seconds: Math.round(elapsed),
      engagement_score: Number(engagement.toFixed(3)),
      started_at: startedAtRef.current,
      ended_at: new Date().toISOString(),
      partial: !!isPartial,
    };
  };

  const finish = () => {
    clearInterval(tickRef.current);
    setRunning(false);
    audio.setEnabled(false);
    onComplete(buildMetrics(false));
  };

  // STOP — snapshot whatever was captured and let the parent jump straight
  // to the partial Session Summary (skips post-symptoms + grounding).
  const abort = () => {
    clearInterval(tickRef.current);
    setRunning(false);
    audio.setEnabled(false);
    onStop(buildMetrics(true));
  };

  const remaining = Math.max(0, duration - elapsed);

  // Keep latest finish() in a ref so the demo's setTimeout never calls a
  // stale closure (the captured `finish` would read mount-time state).
  const finishRef = useRef(finish);
  useEffect(() => {
    finishRef.current = finish;
  });

  // Demo auto-play: shorten duration, turn on pacer, auto-start, simulate
  // alternating taps every 650 ms, then auto-finish at ~10 s.
  const demo = useDemo();
  const demoRanRef = useRef(false);
  useEffect(() => {
    if (!demo.active || demoRanRef.current) return;
    demoRanRef.current = true;
    setDuration(20);
    setBreathingOn(true);
    setTimeout(() => {
      if (!demo.active) return;
      start();
      let i = 0;
      const tapTimer = setInterval(() => {
        if (!demo.active) {
          clearInterval(tapTimer);
          return;
        }
        const side = i % 2 === 0 ? "L" : "R";
        tapsRef.current.push({ side, t: performance.now() });
        setTapCount(tapsRef.current.length);
        if (side === "L") setLeftCount((c) => c + 1);
        else setRightCount((c) => c + 1);
        i++;
      }, 650);
      setTimeout(() => {
        clearInterval(tapTimer);
        if (demo.active) finishRef.current();
      }, 10000);
    }, 1400);
    // No cleanup — see DemoContext for rationale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.active]);

  return (
    <div className="bg-aurora min-h-screen relative overflow-hidden" data-testid="session-mode-a">
      <StopButton onClick={abort} />
      <div className="breathe-halo" />

      {/* HUD top */}
      <div className="relative z-10 max-w-7xl mx-auto pt-8 px-6 sm:px-10 flex items-center justify-between">
        <div className="font-mono text-xs tracking-[0.28em] uppercase text-[color:var(--accent-cyan)]">
          Mode A · Tap-Based
        </div>
        <div className="hidden md:block font-mono text-xs tracking-[0.28em] uppercase text-[color:var(--text-secondary)]">
          Tap LEFT then RIGHT, find your rhythm
        </div>
      </div>

      {/* Instructions panel — shown only before the session starts */}
      {!running && (
        <div className="relative z-20 max-w-3xl mx-auto px-6 sm:px-10 mt-6">
          <Instructions title="How to run Mode A" items={HOW_TO_RUN_MODE_A} />
        </div>
      )}

      {/* Center HUD */}
      <div className="relative z-20 max-w-3xl mx-auto px-6 sm:px-10 mt-6">
        <div className="glass rounded-3xl px-8 py-6 scanlines relative">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-6 items-end">
            <Metric label="Elapsed" value={formatTime(elapsed)} />
            <Metric label="Remaining" value={formatTime(remaining)} />
            <Metric label="Taps" value={tapCount} accent />
            <Metric label="Tap/min" value={tapRate.toFixed(0)} />
            <Metric label="Engage" value={(0.4*consistencyPct + 0.4*alternationPct + 0.2*Math.min(1, tapsRef.current.length / Math.max(1, duration*0.5))).toFixed(2)} />
          </div>
          <div className="mt-4 h-1 w-full bg-[color:var(--bg-soft)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--accent-cyan)]"
              style={{ width: `${(elapsed / duration) * 100}%`, boxShadow: "0 0 12px var(--accent-cyan-glow)" }}
            />
          </div>
          {softPause && running && (
            <div className="mt-3 text-center font-mono text-xs tracking-widest uppercase text-[color:var(--warning)]" data-testid="soft-pause-prompt">
              Pause if you need — resume when ready
            </div>
          )}
          {!running && (
            <div className="mt-5 flex items-center justify-center gap-3">
              <label className="text-xs font-mono tracking-widest uppercase text-[color:var(--text-secondary)]">
                Duration
              </label>
              <select
                className="bg-[color:var(--bg-soft)] border border-[color:var(--hairline)] rounded-full px-3 py-1 font-mono text-sm text-ink focus:outline-none focus:border-[color:var(--accent-cyan)]"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                data-testid="mode-a-duration-select"
              >
                <option value={60}>1 min</option>
                <option value={120}>2 min</option>
                <option value={180}>3 min</option>
                <option value={300}>5 min</option>
              </select>
              <button
                type="button"
                className="btn-primary"
                onClick={start}
                data-testid="mode-a-start-btn"
              >
                Start ▶
              </button>
            </div>
          )}
          {running && (
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setPulseCue((p) => !p)}
                data-testid="mode-a-pulse-toggle"
              >
                Rhythm cue: {pulseCue ? "ON" : "OFF"}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => breathCue.setEnabled(!breathCue.enabled)}
                data-testid="mode-a-breath-cue-toggle"
                title="Soft 'in… / out…' tones synced to the pulse"
              >
                Breath cues: {breathCue.enabled ? "ON" : "OFF"}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={finish}
                data-testid="mode-a-finish-btn"
              >
                Finish ✓
              </button>
            </div>
          )}
        </div>

        {/* Adaptive pacer hint (visible only when preSuds is known) */}
        {adaptive && (
          <div className="mt-4">
            <AdaptivePacerBanner decision={adaptive} preSuds={preSuds} />
          </div>
        )}

        {/* Audio + Breathing controls */}
        <div className="mt-4 flex justify-center">
          <AudioBreathControls
            audio={audio}
            breathingOn={breathingOn}
            setBreathingOn={setBreathingOn}
            pattern={breathPattern}
            setPattern={setBreathPattern}
          />
        </div>
      </div>

      {/* Tap zones */}
      <div className="absolute inset-0 grid grid-cols-2 gap-6 px-4 sm:px-8 pt-[26rem] pb-12 z-10 pointer-events-none">
        <button
          type="button"
          aria-label="Left tap"
          className={`tap-zone pointer-events-auto rounded-3xl flex flex-col items-center justify-center text-ink bg-[color:var(--bg-soft)] border border-[color:var(--hairline)] ${
            running && pulseCue ? "pulse-glow" : ""
          }`}
          style={{ minHeight: "40vh" }}
          onPointerDown={() => tap("L")}
          disabled={!running}
          data-testid="tap-left-zone"
        >
          <div className="font-mono text-[10px] tracking-[0.4em] text-[color:var(--text-secondary)]">
            LEFT
          </div>
          <div className="font-mono text-7xl sm:text-9xl text-cyan-glow mt-2 select-none">
            ◀
          </div>
          <div className="font-mono text-xs tracking-widest text-[color:var(--text-secondary)] mt-3">
            {leftCount} taps
          </div>
        </button>
        <button
          type="button"
          aria-label="Right tap"
          className={`tap-zone pointer-events-auto rounded-3xl flex flex-col items-center justify-center text-ink bg-[color:var(--bg-soft)] border border-[color:var(--hairline)] ${
            running && pulseCue ? "pulse-glow" : ""
          }`}
          style={{ minHeight: "40vh", animationDelay: "0.7s" }}
          onPointerDown={() => tap("R")}
          disabled={!running}
          data-testid="tap-right-zone"
        >
          <div className="font-mono text-[10px] tracking-[0.4em] text-[color:var(--text-secondary)]">
            RIGHT
          </div>
          <div className="font-mono text-7xl sm:text-9xl text-cyan-glow mt-2 select-none">
            ▶
          </div>
          <div className="font-mono text-xs tracking-widest text-[color:var(--text-secondary)] mt-3">
            {rightCount} taps
          </div>
        </button>
      </div>

      {/* Always-on breathing pacer during the active session — sits in the
          natural gap between LEFT/RIGHT tap zones. pointer-events-none means
          taps still register on the buttons even where they overlap. We only
          render once the session is running so the Start button and pre-
          session instructions remain unobstructed. */}
      {breathingOn && running && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[15] pointer-events-none flex flex-col items-center"
          style={{ top: "calc(50% - 10px)" }}
          aria-hidden="true"
          data-testid="mode-a-pacer-section"
        >
          <div
            className="font-mono text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase mb-2 sm:mb-3 text-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full whitespace-nowrap"
            style={{
              color: "var(--royal-blue, #153A79)",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 4px 18px rgba(15,26,46,0.10)",
            }}
          >
            Match your breath to the pulsing light
          </div>
          <ResponsivePacer
            active={breathingOn}
            pattern={breathPattern}
            onPhaseChange={breathCue.notify}
            sizes={PACER_SIZES}
          />
        </div>
      )}

      {/* Tip footer */}
      <div className="fixed bottom-4 left-0 right-0 text-center z-10 px-4">
        <div className="inline-block glass rounded-full px-4 py-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
          Tip · You can also use ← / → arrow keys
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="flex flex-col items-center sm:items-start">
      <div className="text-[10px] font-mono tracking-[0.24em] uppercase text-[color:var(--text-secondary)]">
        {label}
      </div>
      <div
        className={`font-mono text-2xl sm:text-3xl ${
          accent ? "text-cyan-glow" : "text-ink"
        } tracking-wider font-light`}
      >
        {value}
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
